import { useState } from 'react';
import { Point } from '../../../../shared/types/mathTypes.ts';
import { ManualMeasurementVariant } from '../../../../shared/utils/getManualMeasurementVariants.ts';
import { MeasurementContextMenu } from '../../../components/protocol-form/measurementContextMenu.tsx';
import { CustomEquipment, MeasurementOption, Protocol } from '../../../types/sharedTypeImpl.ts';
import { formatDatetime } from '../../../utils/fieldFormats.ts';
import { MachineBadges } from '../../../components/machineBadges.tsx';

interface ContextMenuState {
    location: Point;
    protocol: Protocol;
}


interface Props {
    options?: MeasurementOption[];
    cmmEquipments?: CustomEquipment[];
    isExcelPending: boolean;
    pendingProtocolId?: string;
    onExcelSelect: () => void;
    onProtocolSelect: (protocolId: string) => void;
    onVariantSelect: (protocolId: string, variant: ManualMeasurementVariant) => void;
}

export function ProtocolTable({
    options,
    cmmEquipments,
    isExcelPending,
    pendingProtocolId,
    onExcelSelect,
    onProtocolSelect,
    onVariantSelect,
}: Props) {
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

    const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, protocol: Protocol) => {
        e.preventDefault();
        if (isExcelPending || pendingProtocolId) return;
        setContextMenu({
            location: { x: e.clientX, y: e.clientY },
            protocol,
        });
    };

    const handleMeasureClick = async (protocol: Protocol) => {
        if (isExcelPending || pendingProtocolId) return;
        onProtocolSelect(protocol._id!);
    };

    const handleVariantSelect = async (protocol: Protocol, variant: ManualMeasurementVariant) => {
        setContextMenu(null);
        if (isExcelPending || pendingProtocolId) return;
        onVariantSelect(protocol._id!, variant);
    };

    const isLoading = options === undefined;
    return <>
        <table style={{ width: '1040px' }}>
            <thead>
                <tr>
                    <th>Target</th>
                    <th>Version</th>
                    <th>Date created</th>
                    <th>Last measurement</th>
                    <th>Allowed machines</th>
                    <th>Variant</th>
                    <th>Parts</th>
                    <th />
                    <th style={{ width: '50px' }} />
                </tr>
            </thead>
            {isLoading
                ? 'Loading...'
                : <tbody>
                    <tr>
                        <td>Excel-mittapöytäkirja</td>
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td>
                            <button onClick={onExcelSelect}>
                                {isExcelPending ? 'Adding...' : 'Measure'}
                            </button>
                        </td>
                        <td />
                    </tr>
                    {options?.map(it =>
                        <tr key={it.protocol._id}>
                            <td>{it.protocol.partDisplayName}</td>
                            <td>{it.protocol.version}</td>
                            <td>{formatDatetime(it.protocol.createTime)}</td>
                            <td>{it.latestRecord && formatDatetime(it.latestRecord.measurementStart!)}</td>
                            <td>
                                <MachineBadges
                                    machineIds={it.protocol.allowedMachines ?? []}
                                    equipments={cmmEquipments}
                                    centered
                                />
                            </td>
                            <td>{it.nextVariant ?? ''}</td>
                            <td>{it.partsToMeasure ?? '1'}</td>
                            <td>
                                <button onClick={() => handleMeasureClick(it.protocol)} >
                                    {it.protocol._id === pendingProtocolId ? 'Adding...' : 'Measure'}
                                </button>
                            </td>
                            <td>
                                <button
                                    className='td__menu-button'
                                    onClick={e => handleMenuOpen(e, it.protocol)}
                                >
                                    &#8942;
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
            }
        </table>

        {
            contextMenu && <MeasurementContextMenu
                screenCoords={contextMenu.location}
                protocol={contextMenu.protocol}
                onVariantSelect={handleVariantSelect}
                onClose={() => setContextMenu(null)}
            />
        }
    </>;
}
